import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { validationService } from "../services/validation.service";
import StatusBadge from "../ui/tags/StatusBadge";
import {
  getBulkSearchStatusNotice,
  isBulkSearchFinished,
  isBulkSearchSuccessful,
} from "../utils/bulkSearchStatus";
import styles from "../styles/pages/BulkValidation.module.css";

const POLL_INTERVAL = 7000;

export default function BulkValidationPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();

  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    async function fetchTask() {
      try {
        const data = await validationService.getBulkTask(taskId);
        setTaskData(data);
        if (isBulkSearchFinished(data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchTask();
    pollRef.current = setInterval(fetchTask, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [taskId]);

  const rows = taskData?.rows ?? [];
  const foundRows = rows.filter((row) => row.row_status === "found");

  const isFinished = isBulkSearchFinished(taskData?.status);
  const statusNotice = getBulkSearchStatusNotice(taskData?.status);
  const showResults = rows.length > 0 || isBulkSearchSuccessful(taskData?.status);
  const progress =
    taskData?.total > 0
      ? Math.round((taskData.finished / taskData.total) * 100)
      : 0;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.topBar}>
            <button
              className={styles.backBtn}
              onClick={() => navigate("/database")}
              type="button"
            >
              ← Volver a Database
            </button>
            <div className={styles.pageTitle}>Búsqueda masiva</div>
          </div>

          {loading && (
            <div className={styles.statusCard}>
              <div className={styles.spinner} />
              <span className={styles.statusText}>Cargando...</span>
            </div>
          )}

          {!loading && !isFinished && (
            <div className={styles.statusCard}>
              <div className={styles.spinner} />
              <div className={styles.statusBody}>
                <div className={styles.statusText}>Procesando contactos...</div>
                <div className={styles.statusSub}>
                  {taskData?.finished ?? 0} de {taskData?.total ?? "?"} completados
                </div>
                {taskData?.total > 0 && (
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && isFinished && (
            <>
              {showResults && (
              <div className={styles.summary}>
                Se encontraron{" "}
                <strong>{foundRows.length}</strong> de{" "}
                <strong>{rows.length}</strong> contactos
                {foundRows.length > 0 && taskData?.status === "finished" && (
                  <span className={styles.summaryNote}>
                    {" "}— ya guardados en tu base de contactos
                  </span>
                )}
              </div>
              )}
              {statusNotice && (
                <div className={styles.statusNotice} role="status">
                  {statusNotice}
                </div>
              )}

              {showResults && (
              <div className={styles.tableWrap}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>Nombre</div>
                  <div className={styles.headerCell}>Apellido</div>
                  <div className={styles.headerCell}>Email encontrado</div>
                  <div className={styles.headerCellCenter}>Estado</div>
                </div>

                {rows.map((row) => {
                  const found = row.row_status === "found";
                  return (
                    <div
                      key={row.id}
                      className={`${styles.tableRow} ${found ? "" : styles.tableRowDim}`}
                    >
                      <div className={styles.cell}>{row.first_name ?? "-"}</div>
                      <div className={styles.cell}>{row.last_name ?? "-"}</div>
                      <div className={styles.cell}>{row.email ?? "-"}</div>
                      <div className={styles.cellCenter}>
                        <StatusBadge isActive={found} size={18} />
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
